import { useState, useCallback } from 'react';
import { Plus, Pencil, Trash2, ChevronLeft, ChevronRight, RefreshCw, MapPin, Compass } from 'lucide-react';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { EmptyState } from '@/components/common/EmptyState';
import { TrackFormDialog } from '@/components/tracks/TrackFormDialog';
import { TrackShapeStudioModal } from '@/components/tracks/TrackShapeStudioModal';
import { useToast } from '@/context/ToastContext';
import { useI18n } from '@/context/I18nContext';
import { rpc } from '@/api/client';

interface RaceTrack {
  id: string;
  name: string;
  latitude: string;
  longitude: string;
  zoom?: number;
  sessionTrackIds: string[];
  raceEventTrackNames: string[];
  hasImage?: boolean;
}

interface PaginatedResponse {
  tracks: RaceTrack[];
  totalCount: number;
  page: number;
  pageSize: number;
}

const PAGE_SIZE = 10;

export function TracksTab() {
  const { t } = useI18n();
  const { showToast } = useToast();

  const [items, setItems] = useState<RaceTrack[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editItem, setEditItem] = useState<RaceTrack | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fetchingImageId, setFetchingImageId] = useState<string | null>(null);
  const [imageVersion, setImageVersion] = useState(Date.now());
  const [studioTrack, setStudioTrack] = useState<RaceTrack | null>(null);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const fetchPage = useCallback(async (p: number) => {
    try {
      const data = await rpc<PaginatedResponse>('ListRaceTracks', { page: p, pageSize: PAGE_SIZE });
      setItems(data.tracks ?? []);
      setTotalCount(data.totalCount ?? 0);
      setPage(data.page ?? p);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(() => fetchPage(page), [fetchPage, page]);

  useState(() => { fetchPage(1); });

  const goToPage = (p: number) => {
    const clamped = Math.max(1, Math.min(totalPages, p));
    setPage(clamped);
    fetchPage(clamped);
  };

  const handleFetchImage = async (item: RaceTrack) => {
    setFetchingImageId(item.id);
    try {
      await rpc('FetchRaceTrackImage', {
        id: item.id,
        latitude: parseFloat(item.latitude) || 0,
        longitude: parseFloat(item.longitude) || 0,
        zoom: item.zoom || 0,
      });
      setImageVersion(Date.now());
      showToast(t('tracks.imageFetched'));
      await refresh();
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setFetchingImageId(null);
    }
  };

  const handleSave = async (values: {
    name: string;
    latitude: string;
    longitude: string;
    zoom: number;
    sessionTrackIds: string[];
    raceEventTrackNames: string[];
  }) => {
    if (!values.name.trim()) {
      showToast(t('crud.requiredField', { field: t('tracks.name') }), 'error');
      return;
    }

    setSaving(true);
    try {
      if (editItem) {
        await rpc('UpdateRaceTrack', {
          id: editItem.id,
          name: values.name,
          latitude: parseFloat(values.latitude) || 0,
          longitude: parseFloat(values.longitude) || 0,
          zoom: values.zoom || 0,
          sessionTrackIds: values.sessionTrackIds,
          raceEventTrackNames: values.raceEventTrackNames,
        });
        showToast(t('crud.updated', { entity: t('entity.track') }));
      } else {
        await rpc('CreateRaceTrack', {
          name: values.name,
          latitude: parseFloat(values.latitude) || 0,
          longitude: parseFloat(values.longitude) || 0,
          zoom: values.zoom || 0,
          sessionTrackIds: values.sessionTrackIds,
          raceEventTrackNames: values.raceEventTrackNames,
        });
        showToast(t('crud.created', { entity: t('entity.track') }));
      }
      setImageVersion(Date.now());
      setShowForm(false);
      setEditItem(null);
      await refresh();
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: RaceTrack) => {
    if (!confirm(t('crud.confirmDelete', { entity: t('entity.track').toLowerCase(), name: item.name }))) return;
    try {
      await rpc('DeleteRaceTrack', { id: item.id });
      showToast(t('crud.deleted', { entity: t('entity.track') }));
      await refresh();
    } catch (e) {
      showToast((e as Error).message, 'error');
    }
  };

  const startEdit = (item: RaceTrack) => {
    setEditItem(item);
    setShowForm(true);
  };

  const startAdd = () => {
    setEditItem(null);
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditItem(null);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between gap-2 mb-6 flex-wrap">
        <h2 className="text-xl font-semibold tracking-tight">{t('tab.tracks')}</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={startAdd}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium
              bg-accent text-white hover:bg-accent-hover transition-colors cursor-pointer"
          >
            <Plus size={14} />
            {t('common.add')}
          </button>
        </div>
      </div>

      {/* Form Dialog */}
      {showForm && (
        <TrackFormDialog
          open
          editing={editItem}
          saving={saving}
          onSave={handleSave}
          onClose={cancelForm}
        />
      )}

      {/* Track Shape & Annotation Studio Modal */}
      {studioTrack && (
        <TrackShapeStudioModal
          trackId={studioTrack.id}
          trackName={studioTrack.name}
          latitude={parseFloat(studioTrack.latitude) || 0}
          longitude={parseFloat(studioTrack.longitude) || 0}
          zoom={studioTrack.zoom}
          hasImage={studioTrack.hasImage}
          onClose={() => setStudioTrack(null)}
          onSaved={() => refresh()}
        />
      )}

      {/* Content */}
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorState message={error} />
      ) : totalCount === 0 ? (
        <EmptyState
          icon="🏁"
          message={t('crud.emptyState', { title: t('tab.tracks').toLowerCase(), entity: t('entity.track').toLowerCase() })}
        />
      ) : (
        <div className="card overflow-hidden">
          {/* Desktop table */}
          <table className="w-full border-collapse hidden md:table">
            <thead>
              <tr className="divider">
                <th className="w-[80px] px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                  {t('tracks.satelliteImage')}
                </th>
                <th className="px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                  {t('tracks.name')}
                </th>
                <th className="w-[120px] px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                  {t('tracks.latitude')}
                </th>
                <th className="w-[120px] px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                  {t('tracks.longitude')}
                </th>
                <th className="w-[100px] text-right px-4 py-2.5 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted" />
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={item.id} className={`hover:bg-bg-hover transition-colors ${i < items.length - 1 ? 'divider' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="w-12 h-12 rounded border border-border-subtle overflow-hidden bg-bg-surface flex items-center justify-center relative shrink-0">
                      {item.hasImage ? (
                        <img
                          src={`/rpc/GetTrackImage?trackId=${encodeURIComponent(item.id)}&v=${imageVersion}`}
                          alt={item.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <MapPin size={14} className="text-text-muted" />
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-text-primary">{item.name}</div>
                    {(item.sessionTrackIds?.length > 0 || item.raceEventTrackNames?.length > 0) && (
                      <div className="text-[0.65rem] text-text-muted mt-0.5">
                        {t('tracks.linkedAliases', {
                          sessions: String(item.sessionTrackIds?.length ?? 0),
                          races: String(item.raceEventTrackNames?.length ?? 0),
                        })}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary tabular-nums">{item.latitude}</td>
                  <td className="px-4 py-3 text-sm text-text-secondary tabular-nums">{item.longitude}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setStudioTrack(item)}
                        className="p-1.5 rounded text-text-muted hover:text-accent transition-colors cursor-pointer"
                        title={t('trackStudio.openStudio')}
                      >
                        <Compass size={14} />
                      </button>
                      <button
                        onClick={() => handleFetchImage(item)}
                        disabled={fetchingImageId === item.id || (!parseFloat(item.latitude) && !parseFloat(item.longitude))}
                        className="p-1.5 rounded text-text-muted hover:text-text-primary transition-colors cursor-pointer disabled:opacity-30"
                        title={item.hasImage ? t('tracks.refetchImage') : t('tracks.fetchImage')}
                      >
                        <RefreshCw size={14} className={fetchingImageId === item.id ? 'animate-spin text-accent' : ''} />
                      </button>
                      <button
                        onClick={() => startEdit(item)}
                        className="p-1.5 rounded text-text-muted hover:text-text-primary transition-colors cursor-pointer"
                        title={t('common.edit')}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(item)}
                        className="p-1.5 rounded text-text-muted hover:text-red transition-colors cursor-pointer"
                        title={t('common.delete')}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Mobile list */}
          <div className="md:hidden">
            {items.map((item, i) => (
              <div key={item.id} className={`p-4 hover:bg-bg-hover transition-colors ${i < items.length - 1 ? 'divider' : ''}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="w-16 h-16 rounded border border-border-subtle overflow-hidden bg-bg-surface flex items-center justify-center relative shrink-0">
                    {item.hasImage ? (
                      <img
                        src={`/rpc/GetTrackImage?trackId=${encodeURIComponent(item.id)}&v=${imageVersion}`}
                        alt={item.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <MapPin size={16} className="text-text-muted" />
                    )}
                  </div>
                  <div className="flex-1 space-y-1">
                    <div className="text-sm">
                      <span className="text-text-muted text-xs">{t('tracks.name')}: </span>
                      <span className="text-text-primary">{item.name}</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-text-muted text-xs">{t('tracks.latitude')}: </span>
                      <span className="text-text-primary tabular-nums">{item.latitude}</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-text-muted text-xs">{t('tracks.longitude')}: </span>
                      <span className="text-text-primary tabular-nums">{item.longitude}</span>
                    </div>
                    {(item.sessionTrackIds?.length > 0 || item.raceEventTrackNames?.length > 0) && (
                      <div className="text-[0.65rem] text-text-muted">
                        {t('tracks.linkedAliases', {
                          sessions: String(item.sessionTrackIds?.length ?? 0),
                          races: String(item.raceEventTrackNames?.length ?? 0),
                        })}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button
                      onClick={() => handleFetchImage(item)}
                      disabled={fetchingImageId === item.id || (!parseFloat(item.latitude) && !parseFloat(item.longitude))}
                      className="p-2 rounded text-text-muted hover:text-text-primary transition-colors cursor-pointer disabled:opacity-30"
                      title={item.hasImage ? t('tracks.refetchImage') : t('tracks.fetchImage')}
                    >
                      <RefreshCw size={14} className={fetchingImageId === item.id ? 'animate-spin text-accent' : ''} />
                    </button>
                    <button
                      onClick={() => startEdit(item)}
                      className="p-2 rounded text-text-muted hover:text-text-primary transition-colors cursor-pointer"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(item)}
                      className="p-2 rounded text-text-muted hover:text-red transition-colors cursor-pointer"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-border-subtle text-xs text-text-muted">
              <button
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
                className="p-1 rounded hover:bg-bg-hover disabled:opacity-30 transition-colors cursor-pointer"
              >
                <ChevronLeft size={14} />
              </button>
              <span>{page} / {totalPages}</span>
              <button
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages}
                className="p-1 rounded hover:bg-bg-hover disabled:opacity-30 transition-colors cursor-pointer"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
